import { Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { InventoryClassCodeEnums, InventoryCodeEnums } from 'src/app/enum/inventory-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InventoryTableModel } from 'src/app/models/tables/inventory/inventory-table.model';
import { UserService } from 'src/app/services/authentication/user.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InventoryService } from 'src/app/services/inventory/inventory.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.scss'],
  standalone: false
})
export class InventoryComponent implements OnInit {

  isASADMIN: boolean = false;
  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<InventoryTableModel> = new PagedResponseModel<InventoryTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Inventarios';
  page: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }, {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    },
  ];
  url: string = 'inventory';
  userAuthenticated: UserModel = new UserModel();

  constructor(private headerService: HeaderService,
    private confirmDialogService: ConfirmDialogService,
    private inventoryService: InventoryService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userService: UserService,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsConsultation(),
      this.getInventoriesWithPagination(),
      this.userAuthenticatedIsASADMIN()
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async deleteInventory(inventory: InventoryTableModel) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para eliminar inventarios. Comuníquese con el administrador del sistema.'
      );
      return;
    }

    const isInventorySubrental = inventory.inventoryCode == InventoryCodeEnums.subrental &&
      inventory.inventoryClassId == InventoryClassCodeEnums.subrental;
    if (isInventorySubrental) {
      this.toastHandlerService.showToast(
        'error',
        'No se puede eliminar el inventario principal de subalquiler.'
      );
      return;
    }

    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el inventario?')) {
      try {
        await firstValueFrom(this.inventoryService.deleteInventory(inventory.inventoryId));
        await this.getInventoriesWithPagination();
        this.toastHandlerService.showToast(
          'success',
          'El inventario fue eliminado exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar el inventario.', error);
      }
    }
  }

  newRegister() {
    this.router.navigate([`/${this.url}/register`]);
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'warning',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getPermissionUpdatable(this.url);
  }

  async getColumnsConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.inventoryService.getColumnsConsultation());
      }
    } catch (error) {
      console.error('Error al buscar columnas para la consulta.', error);
    }
  }

  async getInventoriesWithPagination() {
    try {
      this.generalData = await firstValueFrom(
        this.inventoryService.getAllInventoriesWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
    } catch (error) {
      console.error('Error al buscar los inventarios.', error);
    }
  }

  async getPermissionUpdatable(url: string) {
    if (this.isASADMIN) {
      this.permissionUpdatable = true;
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(url));
    } catch (error) {
      console.error('Error al obtener el perfil de permisos por URL.', error);
    }
  }

  async onAction(event: { action: string, item?: InventoryTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.id}`]);
    }
    if (event.action === 'delete' && event.item) {
      await this.deleteInventory(event.item);
    }
  }

  async onPageChange(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    await this.getInventoriesWithPagination();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async userAuthenticatedIsASADMIN() {
    try {
      const response = await firstValueFrom(this.userService.userAuthenticatedIsASADMIN());
      this.isASADMIN = response;
      await this.getPermission();
    } catch (error) {
      console.error('Error al validar si el usuario es ASADMIN.', error);
    }
  }
}
